/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Flex,
    Box,
    Text,
    VStack,
    Fade,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks'

// project components
import ImageGalleryCompact from '../image-gallery-compact'
import {Skeleton as ImageGallerySkeleton} from '@salesforce/retail-react-app/app/components/image-gallery'
import {getPriceData} from '../../utils/product-utils'
import QuantityPicker from '../quantity-picker'
import LoadingSpinner from '../loading-spinner'
import {useGlobaleContext} from '../../contexts/globale'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {ProductViewHeader} from './partials/product-view-header'
import {QuickAddSwatches} from './partials/swatches'

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductViewQuickAdd = forwardRef(
    (
        {
            product: productOriginal,
            category,
            imageSize = 'md',
            globalePrices = null,
            promoId = null,
            isProductLoading,
            isShopTheLook = false,
            productWithFilteredVariants,
            setProductWithFilteredVariants,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            selectedBundleParentQuantity = 1,
            showQtySelector = false,
            enableQtySelector = true,
            showSwatches = true,
            maxOrderQuantity = 1,
            onTileClick = () => {},
            styleVariant = 'default',
            showDescription = false,
            suppressSwatchSpinner = false,
            pwpTargetPromotionId
        },
        ref
    ) => {
        const {countryISO, currencyCode} = useGlobaleContext()
        const {currency: activeCurrency} = useCurrency()
        const intl = useIntl()
        const api = useCommerceApi()
        const {getTokenWhenReady} = useAccessToken()
        const styles = useStyleConfig('ProductViewCompactStyles', {variant: styleVariant})
        const [showChangeSwatchSpinner, setChangeSwatchSpinner] = useState(false)
        const [product, setProduct] = useState(productOriginal)

        const {
            showLoading,
            quantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel
        } = useDerivedProduct(product, true, isProductPartOfBundle, false)
        const priceData = useMemo(() => {
            return getPriceData(productWithFilteredVariants)
        }, [product, quantity, productWithFilteredVariants])
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const isProductASet = product?.type?.set
        const isProductABundle = product?.type?.bundle
        const errorContainerRef = useRef(null)

        const validateAndShowError = () => {
            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            const showError = !isProductASet && !isProductABundle && !hasValidSelection

            toggleShowOptionsMessage(showError)
            return hasValidSelection
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({validateOrderability: validateAndShowError})
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        const productVariationAttributes = variationAttributes
        const selectedProductId = product?.variationValues
            ? product?.variants?.find((v) =>
                  Object.entries(product?.variationValues)?.every(
                      ([k, val]) => v?.variationValues?.[k] === val
                  )
              )?.productId
            : null

        const [selectedVariantId, setSelectedVariantId] = useState(false)
        useEffect(() => {
            const fetchVariant = async () => {
                if (!suppressSwatchSpinner) {
                    setChangeSwatchSpinner(true)
                }
                const token = await getTokenWhenReady()
                const requestParams = {id: selectedProductId}

                if (countryISO && currencyCode) {
                    requestParams.c_selectedCountry = countryISO
                    requestParams.c_selectedCurrency = currencyCode
                }
                try {
                    const variantResponse = await api.shopperProducts.getProduct({
                        parameters: requestParams,
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    })
                    let variants = product.variants?.filter(
                        ({productId}) => productId === selectedVariantId
                    )
                    setProductWithFilteredVariants({
                        ...variantResponse,
                        tieredPrices: variants[0].tieredPrices
                    })

                    setSelectedVariantId(null)
                    if (!suppressSwatchSpinner) {
                        setChangeSwatchSpinner(false)
                    }
                } catch (e) {
                    if (!suppressSwatchSpinner) {
                        setChangeSwatchSpinner(false)
                    }
                }
                return
            }
            if (selectedVariantId) {
                fetchVariant()
            }
        }, [selectedVariantId])

        return (
            <Flex direction={'column'} data-testid="product-view-compact" ref={ref}>
                {showChangeSwatchSpinner && <LoadingSpinner position="fixed" top="auto" />}
                {/* Basic information etc. title, price, breadcrumb*/}
                <Flex
                    direction="row"
                    sx={{
                        '& img': {
                            _focusVisible: {
                                outline: 'none'
                            }
                        }
                    }}
                >
                    {showImageGallery && (
                        <Box width="auto">
                            {product ? (
                                <ImageGalleryCompact
                                    size={imageSize}
                                    isShopTheLook={isShopTheLook}
                                    product={product}
                                    slugUrl={product?.slugUrl}
                                    isProductPartOfSet={true}
                                    imageGroups={product.imageGroups}
                                    selectedVariationAttributes={variationParams}
                                    onTileClick={onTileClick}
                                    styleVariant={styleVariant}
                                />
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>
                    )}

                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack
                        data-invalid={showOptionsMessage}
                        className="product-stack"
                        {...styles.viewWrapper.productStack}
                    >
                        <Box display="block">
                            <ProductViewHeader
                                name={product?.name}
                                product={product}
                                priceData={priceData}
                                globalePrices={globalePrices}
                                promoId={promoId}
                                currency={product?.currency || activeCurrency}
                                category={category}
                                isShopTheLook={isShopTheLook}
                                isProductPartOfBundle={isProductPartOfBundle}
                                showDescription={showDescription}
                                pwpTargetPromotionId={pwpTargetPromotionId}
                            />
                        </Box>
                        <Box
                            className="variations-stack"
                            {...styles.viewWrapper.variationsStack}
                            {...(styleVariant === 'gwp' ? {marginRight: {lg: '-20px'}} : {})}
                        >
                            {isProductPartOfBundle && (
                                <Box>
                                    <Text fontWeight="medium" fontSize="md" aria-label="price">
                                        <label>
                                            {intl.formatMessage({
                                                defaultMessage: 'Quantity',
                                                id: 'product_view.label.quantity'
                                            })}
                                            : {childOfBundleQuantity}
                                        </label>
                                    </Text>
                                </Box>
                            )}
                            <QuickAddSwatches
                                productVariationAttributes={productVariationAttributes}
                                setProduct={setProduct}
                                product={product}
                                setSelectedVariantId={setSelectedVariantId}
                                variant={variant}
                                showOptionsMessage={showOptionsMessage}
                                productWithFilteredVariants={productWithFilteredVariants}
                                showSwatches={showSwatches}
                                showLoading={showLoading}
                            />
                            <Box ref={errorContainerRef}>
                                {!showLoading && showOptionsMessage && (
                                    <Fade in={true}>
                                        <Text {...styles.viewWrapper.invalidSelection} mt={2}>
                                            {intl.formatMessage({
                                                defaultMessage:
                                                    'Please select all your options above'
                                            })}
                                        </Text>
                                    </Fade>
                                )}
                            </Box>
                        </Box>

                        <Box marginLeft="20px" className="product-view__actions">
                            <Box {...styles.actionWrapper}>
                                {showQtySelector && (
                                    <QuantityPicker
                                        id="quantity"
                                        step={1}
                                        value={quantity}
                                        min={1}
                                        max={maxOrderQuantity}
                                        isDisabled={!enableQtySelector}
                                        onChange={(stringValue, numberValue) => {
                                            // Set the Quantity of product to value of input if value number
                                            if (numberValue >= 0) {
                                                setQuantity(numberValue)
                                            } else if (stringValue === '') {
                                                // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                                // User will not be able to add '' quantity to the cart due to the add to cart button enablement rules
                                                setQuantity(stringValue)
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Default to 1the `minOrderQuantity` if a user leaves the box with an invalid value
                                            const value = e.target.value
                                            if (parseInt(value) < 0 || value === '') {
                                                setQuantity(1)
                                            }
                                        }}
                                        onFocus={(e) => {
                                            // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
                                            // new quantity with one click. NOTE: This is something that can be refactored into the parent
                                            // component, potentially as a prop called `selectInputOnFocus`.
                                            e.target.select()
                                        }}
                                        productName={product?.name}
                                    />
                                )}
                            </Box>
                        </Box>
                    </VStack>
                </Flex>
            </Flex>
        )
    }
)

ProductViewQuickAdd.displayName = 'ProductViewQuickAdd'

ProductViewQuickAdd.propTypes = {
    product: PropTypes.object,
    globalePrices: PropTypes.any,
    promoId: PropTypes.any,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    ispdp: PropTypes.bool,
    productWithFilteredVariants: PropTypes.any,
    setProductWithFilteredVariants: PropTypes.func,
    meterType: PropTypes.any,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    index: PropTypes.any,
    onFavouriteToggle: PropTypes.func,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    isShopTheLook: PropTypes.bool,
    selectedBundleParentQuantity: PropTypes.number,
    onTileClick: PropTypes.func,
    showQtySelector: PropTypes.bool,
    enableQtySelector: PropTypes.bool,
    showAddToBag: PropTypes.bool,
    maxOrderQuantity: PropTypes.number,
    styleVariant: PropTypes.string,
    enableAddToBag: PropTypes.bool,
    showSwatches: PropTypes.bool,
    showDescription: PropTypes.bool,
    limitReached: PropTypes.bool,
    showAddedToBagLabel: PropTypes.bool,
    suppressSwatchSpinner: PropTypes.bool,
    pwpTargetPromotionId: PropTypes.string
}

export default ProductViewQuickAdd
